import { Controller } from "@hotwired/stimulus"

// Location Search Controller for smart location filtering
// Connects to data-controller="location-search"
export default class extends Controller {
  static targets = ["input", "suggestions", "selected", "hiddenInputs", "loading"]
  static values = { 
    selected: Array,
    url: String 
  }

  connect() {
    
    this.selections = new Set(this.selectedValue || [])
    
    this.searchTimeout = null
    
    this.currentSelection = -1
    
    this.updateSelectedDisplay()
    this.updateHiddenInputs()
    
    // Close suggestions when clicking outside
    this.boundCloseOnClickOutside = this.closeOnClickOutside.bind(this)
    document.addEventListener('click', this.boundCloseOnClickOutside)
  }

  disconnect() {
    
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    
    document.removeEventListener('click', this.boundCloseOnClickOutside)
  }

  search(event) {
    const query = event.target.value.trim()
    
    // Clear previous timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    
    // Reset keyboard selection
    this.currentSelection = -1
    
    if (query.length < 2) {
      this.hideSuggestions()
      return
    }
    
    this.showLoading()
    
    // Debounce the search
    this.searchTimeout = setTimeout(() => {
      this.fetchSuggestions(query)
    }, 300)
  }

  async fetchSuggestions(query) {
    try {
      const response = await fetch(`${this.urlValue}?q=${encodeURIComponent(query)}`)
      const suggestions = await response.json()
      
      this.displaySuggestions(suggestions)
      this.hideLoading()
    } catch (error) {
      console.error('Failed to fetch location suggestions:', error)
      this.hideLoading()
      this.hideSuggestions()
    }
  }

  displaySuggestions(suggestions) {
    if (!this.hasSuggestionsTarget) return
    
    if (suggestions.length === 0) {
      this.hideSuggestions()
      return
    }
    
    const html = suggestions.map((suggestion, index) => {
      const icon = suggestion.type === 'province' ? 'ph-map-trifold' : 'ph-buildings'
      const colorClass = suggestion.type === 'province' ? 'text-blue-600' : 'text-green-600'

      return `
        <div class="suggestion-item px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center justify-between border-b border-gray-100 last:border-b-0"
             data-index="${index}"
             data-value="${suggestion.value}"
             data-action="click->location-search#selectSuggestion">
          <div class="flex items-center">
            <i class="ph-fill ${icon} ${colorClass} mr-3 w-4"></i>
            <div>
              <div class="text-sm font-medium text-gray-900">${suggestion.label}</div>
              <div class="text-xs text-gray-500">${suggestion.description}</div>
            </div>
          </div>
          <i class="ph-fill ph-plus text-gray-400 text-xs"></i>
        </div>
      `
    }).join('')
    
    this.suggestionsTarget.innerHTML = html
    this.showSuggestions()
  }

  selectSuggestion(event) {
    event.preventDefault()
    
    const suggestionElement = event.currentTarget
    const value = suggestionElement.dataset.value
    
    if (value && !this.selections.has(value)) {
      this.selections.add(value)
      this.updateSelectedDisplay()
      this.updateHiddenInputs()
      this.triggerFilterUpdate()
    }
    
    // Clear input and hide suggestions
    this.inputTarget.value = ''
    this.hideSuggestions()
  }

  removeLocation(event) {
    event.preventDefault()
    
    const location = event.currentTarget.dataset.location
    if (this.selections.has(location)) {
      this.selections.delete(location)
      this.updateSelectedDisplay()
      this.updateHiddenInputs()
      this.triggerFilterUpdate()
    }
  }

  updateSelectedDisplay() {
    if (!this.hasSelectedTarget) return
    
    if (this.selections.size === 0) {
      this.selectedTarget.innerHTML = ''
      return
    }
    
    const html = Array.from(this.selections).map(location => {
      const isProvince = !location.includes(', ')
      const colorClass = isProvince ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
      const icon = isProvince ? 'ph-map-trifold' : 'ph-buildings'

      return `
        <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colorClass}">
          <i class="ph-fill ${icon} mr-1"></i>
          ${location}
          <button type="button" 
                  class="ml-1 text-current hover:text-opacity-75"
                  data-location="${location}"
                  data-action="click->location-search#removeLocation">
            ×
          </button>
        </span>
      `
    }).join('')
    
    this.selectedTarget.innerHTML = html
  }

  updateHiddenInputs() {
    if (!this.hasHiddenInputsTarget) return
    
    // Clear existing hidden inputs
    this.hiddenInputsTarget.innerHTML = ''
    
    // Create new hidden inputs for each selection
    Array.from(this.selections).forEach(location => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'location[]'
      input.value = location
      this.hiddenInputsTarget.appendChild(input)
    })
  }

  triggerFilterUpdate() {
    const changeEvent = new CustomEvent('location-search:change', {
      bubbles: true,
      detail: { selections: Array.from(this.selections) }
    })
    
    this.element.dispatchEvent(changeEvent)
    
    const filterController = this.application.getControllerForElementAndIdentifier(
      document.querySelector('[data-controller*="filter"]'), 'filter'
    )
    if (filterController && typeof filterController.filter === 'function') {
      filterController.filter()
    }
  }

  showSuggestions() {
    if (this.hasSuggestionsTarget) {
      this.suggestionsTarget.classList.remove('hidden')
    }
  }

  hideSuggestions() {
    if (this.hasSuggestionsTarget) {
      this.suggestionsTarget.classList.add('hidden')
    }
  }

  showSuggestions() {
    if (this.hasSuggestionsTarget) {
      this.suggestionsTarget.classList.remove('hidden')
    }
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove('hidden')
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('hidden')
    }
  }

  closeOnClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideSuggestions()
    }
  }

  // Keyboard navigation support
  keydown(event) {
    if (!this.hasSuggestionsTarget || this.suggestionsTarget.classList.contains('hidden')) {
      return
    }
    
    const suggestions = this.suggestionsTarget.querySelectorAll('.suggestion-item')
    
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.currentSelection = Math.min(this.currentSelection + 1, suggestions.length - 1)
        this.updateKeyboardSelection(suggestions)
        break
      case 'ArrowUp':
        event.preventDefault()
        this.currentSelection = Math.max(this.currentSelection - 1, -1)
        this.updateKeyboardSelection(suggestions)
        break
      case 'Enter':
        event.preventDefault()
        if (this.currentSelection >= 0 && suggestions[this.currentSelection]) {
          suggestions[this.currentSelection].click()
        }
        break
      case 'Escape':
        event.preventDefault()
        this.hideSuggestions()
        this.inputTarget.blur()
        break
    }
  }

  updateKeyboardSelection(suggestions) {
    // Remove previous selection
    suggestions.forEach(item => item.classList.remove('bg-blue-50'))
    
    // Add selection to current item
    if (this.currentSelection >= 0 && suggestions[this.currentSelection]) {
      suggestions[this.currentSelection].classList.add('bg-blue-50')
    }
  }
}